import { FormEvent, ReactNode, useEffect, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { logout, useAuth } from '../lib/auth'
import { useSettings } from '../lib/settings'
import { imageUrl, tagPath } from '../lib/format'
import { Icon } from './Icon'
import { NewsletterForm } from './NewsletterForm'

const SITE = 'What a comical life'

/** The site name, broken over two lines, with the optional title image above it (set in Settings) */
export function SiteTitle({ image = true }: { image?: boolean }) {
  const { settings } = useSettings()
  return (
    <Link to="/" className="site-title">
      {image && settings.titleImage && <img className="site-title-img" src={imageUrl(settings.titleImage.key)} alt={settings.titleImage.altText} />}
      <span>What a</span> <span>comical life</span>
    </Link>
  )
}

function SearchBox({ onDone }: { onDone?: () => void }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const location = useLocation()
  const [q, setQ] = useState(location.pathname === '/search' ? params.get('q') ?? '' : '')
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!q.trim()) return
    navigate(`/search?q=${encodeURIComponent(q.trim())}`)
    onDone?.()
  }
  return (
    <form role="search" className="searchbox" onSubmit={submit}>
      <input type="search" aria-label="Search comics" placeholder="Search comics" value={q} onChange={(e) => setQ(e.target.value)} />
      <button type="submit" aria-label="Search">
        <Icon name="search" size={18} />
      </button>
    </form>
  )
}

function Nav() {
  const { isArtist } = useAuth()
  const { settings } = useSettings()
  const location = useLocation()
  const cls = ({ isActive }: { isActive: boolean }) => (isActive ? 'navlink active' : 'navlink')
  const currentTag = location.pathname.startsWith('/tag/') ? decodeURIComponent(location.pathname.slice(5)) : ''
  return (
    <nav aria-label="Main" className="nav">
      <NavLink to="/" end className={cls}>
        Home
      </NavLink>
      {settings.sidebarTags.map((tag) => (
        <Link
          key={tag}
          to={tagPath(tag)}
          className={currentTag.toLowerCase() === tag.toLowerCase() ? 'navlink active' : 'navlink'}
          aria-current={currentTag.toLowerCase() === tag.toLowerCase() ? 'page' : undefined}
        >
          {tag}
        </Link>
      ))}
      <div className="navsep" />
      <NavLink to="/about" className={cls}>
        About
      </NavLink>
      <NavLink to="/contact" className={cls}>
        Contact
      </NavLink>
      {isArtist && (
        <>
          <div className="navlabel">Artist</div>
          <NavLink to="/artist/upload" className={cls}>
            Upload comic
          </NavLink>
          <NavLink to="/artist/comics" className={cls}>
            Manage comics
          </NavLink>
          <NavLink to="/artist/tags" className={cls}>
            Manage tags
          </NavLink>
          <NavLink to="/artist/comments" className={cls}>
            Comments
          </NavLink>
          <NavLink to="/artist/newsletter" className={cls}>
            Newsletter
          </NavLink>
          <NavLink to="/artist/settings" className={cls}>
            Settings
          </NavLink>
          <button type="button" className="navlink" onClick={logout}>
            Log out
          </button>
        </>
      )}
    </nav>
  )
}

/** Every page: sidebar on desktop and iPad, a top bar with a menu on phones */
export function Layout({ children, footer = 'reader' }: { children: ReactNode; footer?: 'reader' | 'artist' }) {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  useEffect(() => {
    setOpen(false)
  }, [location.pathname, location.search])
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <div className="shell">
      <a className="skip" href="#content">
        Skip to content
      </a>
      <aside className="sidebar">
        <SiteTitle />
        <SearchBox />
        <Nav />
      </aside>

      <header className="topbar">
        <SiteTitle image={false} />
        <button type="button" className="menu-button" aria-label={open ? 'Close menu' : 'Menu'} aria-expanded={open} onClick={() => setOpen(!open)}>
          <Icon name={open ? 'close' : 'menu'} size={26} />
        </button>
      </header>
      {open && (
        <div className="mobile-menu">
          <div className="panel-area">
            <SearchBox onDone={() => setOpen(false)} />
            <Nav />
          </div>
          <button type="button" className="scrim-area" aria-label="Close menu" onClick={() => setOpen(false)} />
        </div>
      )}

      <main className="main" id="content" tabIndex={-1}>
        {children}
        <footer className="footer">
          {footer === 'reader' && <NewsletterForm />}
          <div className="copyright">&copy; {new Date().getFullYear()} {SITE}</div>
        </footer>
      </main>
    </div>
  )
}
